import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { batchesService, marketTicksService, riskThresholdsService, volatilityService } from '../../services/metalguard';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatus, SUB_STATUS } from '../../hooks/useSubscriptionStatus';
import SubscriptionBanner from 'components/SubscriptionBanner';

import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import { SkeletonCard, SkeletonRow, SkeletonChart } from '../../components/skeletons';

import InventoryOverviewCards from './components/InventoryOverviewCards';
import StockTable from './components/StockTable';
import RiskConcentrationPanel from './components/RiskConcentrationPanel';
import InventoryAgePanel from './components/InventoryAgePanel';
import StressTestSummary from './components/StressTestSummary';
import MarkAsSoldModal from './components/MarkAsSoldModal';
import BatchTimelineModal from 'components/BatchTimelineModal';

const ACTIVE_STATUSES = ['En Espera', 'Procesando', 'Listo para Venta'];

function calcAge(createdAt) {
  if (!createdAt) return 0;
  return Math.floor((Date.now() - new Date(createdAt)?.getTime()) / (1000 * 60 * 60 * 24));
}

export default function InventoryManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [riskThresholds, setRiskThresholds] = useState(null);
  const [volatilitySettings, setVolatilitySettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [soldModal, setSoldModal] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [timelineBatch, setTimelineBatch] = useState(null);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [batchRes, tickRes, threshRes, volRes] = await Promise.all([
        batchesService?.getAll(),
        marketTicksService?.getLatest(),
        riskThresholdsService?.get(),
        volatilityService?.getAll(),
      ]);
      if (!batchRes?.error) setBatches(batchRes?.data || []);
      if (!tickRes?.error) setBenchmarks(tickRes?.data || []);
      if (!threshRes?.error) setRiskThresholds(threshRes?.data);
      if (!volRes?.error) setVolatilitySettings(volRes?.data || []);
      setLastSync(new Date());
    } catch (err) {
      console.error('Error loading inventory data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscription: batches — granular row updates
  const { status: batchesSubStatus, subWarningBanner: batchesWarning, retryNow: retryBatches } = useSubscriptionStatus({
    channelName: `inventory_batches_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'batches',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT') {
        setBatches(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        // Merge payload.new into the existing raw DB row so partial Realtime
        // payloads (when REPLICA IDENTITY FULL is not yet active on the live DB)
        // don't overwrite fields with undefined/null values.
        setBatches(prev => prev?.map(b =>
          b?.id === payload?.new?.id ? { ...b, ...payload?.new } : b
        ));
      } else if (payload?.eventType === 'DELETE') {
        setBatches(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadData,
  });

  // Subscription: market_ticks — granular benchmark updates for exposure, latent P&L, stress test
  const { status: ticksSubStatus, subWarningBanner: ticksWarning, retryNow: retryTicks } = useSubscriptionStatus({
    channelName: `inventory_ticks_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'market_ticks',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        const tick = payload?.new;
        if (!tick) return;
        setBenchmarks(prev => {
          const existing = prev?.find(b => b?.symbol === tick?.symbol);
          if (existing) {
            return prev?.map(b => b?.symbol === tick?.symbol ? { ...b, ...tick } : b);
          }
          return [tick, ...prev];
        });
        setLastSync(new Date());
      } else if (payload?.eventType === 'DELETE') {
        setBenchmarks(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadData,
  });

  // Combined status for banner
  const combinedSubStatus = (
    batchesSubStatus === SUB_STATUS?.FAILED || ticksSubStatus === SUB_STATUS?.FAILED
  ) ? SUB_STATUS?.FAILED : (
    batchesSubStatus === SUB_STATUS?.CONNECTED && ticksSubStatus === SUB_STATUS?.CONNECTED
  ) ? SUB_STATUS?.CONNECTED : (
    batchesSubStatus === SUB_STATUS?.CONNECTING || ticksSubStatus === SUB_STATUS?.CONNECTING
  ) ? SUB_STATUS?.CONNECTING : SUB_STATUS?.RECONNECTING;

  const combinedWarning = (() => {
    if (batchesWarning?.visible || ticksWarning?.visible) {
      const isError = batchesWarning?.type === 'error' || ticksWarning?.type === 'error';
      return {
        visible: true,
        type: isError ? 'error' : 'warning',
        message: isError
          ? 'No se pudo reconectar. Los datos pueden no estar actualizados.'
          : 'Conexión en tiempo real interrumpida. Reintentando...',
      };
    }
    return { visible: false, type: null, message: null };
  })();

  const retryAll = useCallback(() => {
    retryBatches();
    retryTicks();
  }, [retryBatches, retryTicks]);

  const handleEditBatch = useCallback(async (id, updates) => {
    await batchesService?.update(id, updates);
    await loadData();
  }, [loadData]);

  const handleDeleteBatch = useCallback(async (id) => {
    await batchesService?.delete(id);
    setBatches(prev => prev?.filter(b => b?.id !== id));
  }, []);

  const handleSoldSuccess = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Compute overview stats
  const stats = useMemo(() => {
    const activeBatches = batches?.filter(b => ACTIVE_STATUSES?.includes(b?.batch_status));
    const totalWeight = activeBatches?.reduce((s, b) => s + (parseFloat(b?.weight_t) || 0), 0);
    const totalExposure = activeBatches?.reduce((s, b) => s + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0);
    const activeLots = activeBatches?.length;
    const ages = activeBatches?.map(b => calcAge(b?.created_at));
    const avgAge = ages?.length > 0 ? ages?.reduce((s, a) => s + a, 0) / ages?.length : 0;
    return { totalWeight, totalExposure, activeLots, avgAge };
  }, [batches]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-6 py-6">

          {/* Breadcrumb + Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                <Link to="/main-dashboard" className="hover:text-foreground transition-colors">Dashboard</Link>
                <Icon name="ChevronRight" size={12} color="currentColor" />
                <span className="text-foreground font-medium">Inventario</span>
              </nav>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontWeight: 700 }}>Gestión de Inventario</h1>
              <p className="text-sm text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Monitoreo de stock y análisis de riesgo en tiempo real</p>
            </div>
            <div className="flex items-center gap-3">
              {lastSync && (
                <Tooltip
                  title={
                    combinedSubStatus === SUB_STATUS?.CONNECTED
                      ? 'Conexión en tiempo real activa'
                      : combinedSubStatus === SUB_STATUS?.FAILED
                      ? 'Conexión perdida' :'Reconectando...'
                  }
                  description={
                    combinedSubStatus === SUB_STATUS?.CONNECTED
                      ? 'Los datos de inventario y benchmarks se actualizan automáticamente en tiempo real vía Supabase Realtime.'
                      : combinedSubStatus === SUB_STATUS?.FAILED
                      ? 'No se pudo mantener la conexión en tiempo real. Usa el botón Sincronizar para actualizar manualmente.'
                      : 'Intentando restablecer la conexión en tiempo real...'
                  }
                >
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      combinedSubStatus === SUB_STATUS?.CONNECTED ? 'bg-emerald-500 animate-pulse' :
                      combinedSubStatus === SUB_STATUS?.FAILED ? 'bg-red-500' : 'bg-amber-400 animate-pulse'
                    }`} />
                    {combinedSubStatus === SUB_STATUS?.CONNECTED ? 'EN VIVO' : 'Actualizado'} {lastSync?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </Tooltip>
              )}
              <Tooltip
                title="Sincronizar datos"
                description="Recarga manualmente todos los lotes y benchmarks desde la base de datos. Útil si la conexión en tiempo real se interrumpió."
              >
                <button
                  onClick={loadData}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  <Icon name="RefreshCw" size={14} color="currentColor" />
                  Sincronizar
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="mb-6" data-tour="inventory-exposure">
            <SubscriptionBanner status={combinedSubStatus} subWarningBanner={combinedWarning} retryNow={retryAll} />
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 4 })?.map((_, i) => (
                  <SkeletonCard key={i} height="110px" />
                ))}
              </div>
            ) : (
              <InventoryOverviewCards stats={stats} loading={false} />
            )}
          </div>

          {/* Stock Table */}
          <div className="mb-6">
            {loading ? (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <SkeletonCard height="28px" width="200px" rounded="rounded-md" />
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-border">
                    {Array.from({ length: 8 })?.map((_, i) => (
                      <SkeletonRow key={i} cols={7} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <StockTable
                batches={batches}
                benchmarks={benchmarks}
                onMarkSold={(batch) => setSoldModal(batch)}
                onEditBatch={handleEditBatch}
                onDeleteBatch={handleDeleteBatch}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onNavigateBatches={() => navigate('/active-batches')}
                onViewTimeline={setTimelineBatch}
              />
            )}
          </div>

          {/* Risk + Age panels */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            {loading ? (
              <>
                <SkeletonChart height="220px" />
                <SkeletonChart height="220px" />
              </>
            ) : (
              <>
                <RiskConcentrationPanel batches={batches} riskThresholds={riskThresholds} />
                <InventoryAgePanel batches={batches} riskThresholds={riskThresholds} />
              </>
            )}
          </div>

          {/* Stress Test */}
          <div className="mb-6">
            {loading ? (
              <SkeletonChart height="160px" />
            ) : (
              <StressTestSummary batches={batches} volatilitySettings={volatilitySettings} />
            )}
          </div>

        </div>
      </main>
      {/* Mark as Sold Modal */}
      {soldModal && (
        <MarkAsSoldModal
          batch={soldModal}
          onClose={() => setSoldModal(null)}
          onSuccess={handleSoldSuccess}
        />
      )}
      {timelineBatch && (
        <BatchTimelineModal
          batchId={timelineBatch?.id}
          batchRaw={timelineBatch}
          onClose={() => setTimelineBatch(null)}
        />
      )}
    </div>
  );
}
